<template>
  <v-content class="opportunity page">
    <can I="read" a="Opportunity">
      <v-container v-if="opportunity">
        <v-toolbar flat class="transparent">
          <v-breadcrumbs
            class="pl-0"
            :items="[
              { exact: true, text: 'Admin', to: '/dashboard' },
              { exact: true, text: 'Opportunities', to: '/opportunities' },
              { exact: true, text: $route.params.id, to: `/opportunities/${$route.params.id}` },
            ]"
          >
            <v-icon slot="divider">mdi-chevron-right</v-icon>
          </v-breadcrumbs>

          <v-spacer />

          <can I="read" a="Analytics">
            <v-tooltip bottom>
              <v-btn slot="activator" small flat fab :to="`/opportunities/${$route.params.id}/demographics`">
                <v-icon>mdi-google-analytics</v-icon>
              </v-btn>
              <span>View eligible candidate demographics</span>
            </v-tooltip>
          </can>
          <can I="create" a="Opportunity">
            <v-tooltip bottom>
              <v-btn slot="activator" small flat fab @click="duplicate">
                <v-icon>mdi-content-duplicate</v-icon>
              </v-btn>
              <span>Duplicate this opportunity</span>
            </v-tooltip>
          </can>
          <can I="batch" a="Opportunity">
            <v-tooltip bottom>
              <v-btn slot="activator" small flat fab :href="`/opportunities/${$route.params.id}/batch`">
                <v-icon>mdi-google-circles-group</v-icon>
              </v-btn>
              <span>Batch move candidates</span>
            </v-tooltip>
          </can>
          <can I="ats" a="Opportunity">
            <v-tooltip bottom>
              <v-btn slot="activator" small flat fab :href="`/opportunities/${$route.params.id}/applicants`">
                <v-icon>mdi-account-group</v-icon>
              </v-btn>
              <span>View this ATS</span>
            </v-tooltip>
          </can>
          <v-tooltip bottom>
            <v-btn slot="activator" small flat fab :href="`//leap.ly/opportunities/${$route.params.id}`">
              <v-icon>mdi-eye</v-icon>
            </v-btn>
            <span>View this opportunity</span>
          </v-tooltip>
          <v-btn small flat fab @click="refresh" @click.right.prevent="touch">
            <v-icon>mdi-refresh</v-icon>
          </v-btn>
          <CopyButton />
        </v-toolbar>

        <can I="edit" a="Opportunity">
          <v-dialog persistent max-width="80%" v-model="editing">
            <Edit
              v-if="editing"
              @refresh="refresh"
              @publish="publish"
              @update="update"
              @cancel="cancel"
            />
          </v-dialog>
        </can>
        <Show @refresh="refresh" @destroy="destroy" @edit="editing = true" />

        <Eligibilities />
        <Spreadsheet />
        <OpportunityManagers />
        <OpportunityLocations />

        <v-toolbar class="transparent elevation-0 my-3">
          <div class="headline">Requirements</div>
          <v-spacer />
          <can I="edit" a="Opportunity">
            <v-menu left>
              <v-btn round slot="activator" color="secondary">
                <v-icon>mdi-plus</v-icon> Add Group
              </v-btn>
              <v-list>
                <v-list-tile @click="addRequirementGroup('ProfileRequirementGroup')">
                  <v-list-tile-title>Profile requirements</v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="addRequirementGroup('ProfileQualificationRequirementGroup')">
                  <v-list-tile-title>Qualification requirements</v-list-tile-title>
                </v-list-tile>
                <v-list-tile @click="addRequirementGroup('ExperienceRequirementGroup')">
                  <v-list-tile-title>Experience requirements</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </can>
        </v-toolbar>

        <RequirementGroup
          v-for="group in opportunity.requirementGroups"
          :value="group"
          :key="`group-${group.id}`"
        />

        <v-toolbar class="transparent elevation-0 my-3">
          <div class="headline">Phases</div>
          <v-spacer />
          <can I="edit" a="Opportunity">
            <v-btn round @click="addPhase" color="secondary">
              <v-icon>mdi-plus</v-icon> Add Phase
            </v-btn>
          </can>
        </v-toolbar>

        <OpportunityPhase
          v-for="phase in opportunity.phases"
          :phase="phase"
          :key="`phase-${phase.id}`"
        />

        <v-toolbar class="transparent elevation-0 my-3">
          <div class="headline">Review Template</div>
          <v-spacer />
          <can I="edit" a="Opportunity">
            <v-btn round color="secondary" @click="editingReviewTemplate = true">
              Edit
            </v-btn>
          </can>
        </v-toolbar>

        <v-card dark>
          <v-card-title v-if="opportunity.reviewTemplate == null">
            No Review Template, click Edit to add one.
          </v-card-title>
          <v-card-text v-else>
            <Markdown :html="false" :source="opportunity.reviewTemplate" class="markdown" />
          </v-card-text>
          <v-divider v-if="editingReviewTemplate" />
          <v-card-text v-if="editingReviewTemplate">
            <v-textarea outline auto-grow persistent-hint
              label="Starting Template"
              v-model="opportunity.reviewTemplate"
              hint="You can provide a message body using Markdown"
            />
          </v-card-text>
          <v-card-actions v-if="editingReviewTemplate">
            <v-spacer />
            <v-btn flat @click="cancel" color="grey">
              Cancel
            </v-btn>
            <v-btn @click="update" color="primary">
              Save
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-container>
    </can>
  </v-content>
</template>

<script>
// Components
import Show from '@/components/opportunities/Show'
import Edit from '@/components/opportunities/Edit'
import Eligibilities from '@/components/opportunities/Eligibilities'
import OpportunityLocations from '@/components/opportunities/OpportunityLocations'
import OpportunityManagers from '@/components/opportunities/OpportunityManagers'
import OpportunityPhase from '@/components/opportunities/OpportunityPhase'
import RequirementGroup from '@/components/opportunities/RequirementGroup'
import Spreadsheet from '@/components/opportunities/Spreadsheet.vue'
import Markdown from 'vue-markdown'

export default {
  components: {
    Show,
    Edit,
    Eligibilities,
    OpportunityLocations,
    OpportunityManagers,
    OpportunityPhase,
    RequirementGroup,
    Spreadsheet,
    Markdown,
  },
  data() {
    return {
      editing: false,
      editingReviewTemplate: false,
    }
  },
  created() {
    this.$store.dispatch('admin/opportunities/fetch', this.$route.params.id)
  },
  computed: {
    opportunity() {
      return this.$store.getters['admin/opportunities/find']()
    },
  },
  methods: {
    refresh() {
      return this.$store.dispatch('admin/opportunities/fetch', this.$route.params.id)
    },
    touch() {
      return this.$store.dispatch('admin/opportunities/touch', this.$route.params.id)
    },
    duplicate() {
      if (!this.$can('create', 'Opportunity')) return

      this.$http
        .post(`/admin/opportunities?from=${this.$route.params.id}`)
        .then(({data}) => {
          this.$store.dispatch('snackbarSuccess', 'Duplicated successfully')
          this.$router.push(`/opportunities/${data.id}`)
        }, errors => this.$store.dispatch('snackbarError', 'Duplication failed'))
    },
    cancel() {
      this.refresh().then(() => {
        this.editing = false
        this.editingReviewTemplate = false
      })
    },
    publish() {
      this.$store
        .dispatch(
          'admin/opportunities/update',
          Object.assign(this.opportunity, { publishedAt: this.$date(new Date()) })
        )
        .then(() => this.$store.dispatch('snackbarSuccess', 'Published successfully'))
        .catch(() => this.$store.dispatch('snackbarError', 'Organization not published'))
    },
    update() {
      this.$store.dispatch('admin/opportunities/update', this.opportunity).then(() => {
        if (this.opportunity.errors) {
          this.$store.commit('snackbar', {
            text: 'There was a problem updating the opportunity',
            type: 'error',
          })
        } else {
          this.$store.commit('snackbar', {
            text: 'Opportunity was updated successfully',
            type: 'success',
          })
          this.editing = false
          this.editingReviewTemplate = false
        }
      })
    },
    destroy() {
      this.$store.dispatch('admin/opportunities/destroy', this.$route.params.id).then(
        () => {
          // Success
          this.$store.commit('snackbar', {
            text: 'Opportunity deleted',
            type: 'success',
          })
          this.$router.push('/opportunities')
        },
        () => {
          // Failure
          this.$store.commit('snackbar', {
            text: 'There was a problem deleting the opportunity',
            type: 'error',
          })
        }
      )
    },
    addRequirementGroup(type) {
      this.$store
        .dispatch('admin/opportunities/createRequirementGroup', {
          type,
          mandatory: true,
          opportunity: { id: this.$route.params.id },
        })
        .then(() =>
          this.$store.commit('snackbar', {
            text: 'Requirement group added',
            type: 'success',
          })
        )
    },
    addPhase() {
      this.$store
        .dispatch('admin/opportunities/createPhase', {
          opportunity: { id: this.$route.params.id },
        })
        .then(() =>
          this.$store.commit('snackbar', {
            text: 'Opportunity phase added',
            type: 'success',
          })
        )
    },
  },
}
</script>
